import NotificationActions from './NotificationActions';
import ActionInProgressActions from './ActionInProgressActions';
import generateUuid from 'tiny-uuid';
import ErrorHandler from '../Utilities/ErrorHandler';

/// Use executeRequest if you have an asynchronous action to perform and don't require any custom error handling.
/// When used in conjunction with the DisableOnActionInProgress component any buttons inside will be disabled at the start of the action and be re-enabled when the action completes/errors.
/// Any errors returned will be displayed to the user via a notification.
/// You can provide a (optional) success title and message to be displayed to the user when/if the task completes with no errors.

/// Use executeRequestWithNoActionNotification is the same as executeRequest but does not generate an ActionInOProgress action. This would typically
/// be used by peripheral non-ui generated requests e.g. the MasterPageView initiating requests behind the scenes to load the user profile when navigating to a new page
/// as opposed to a request directly generated when a user clicks on a button.

/// If you want more customisation (for instance choosing when and what to notify the user) and/or wish to perfom any additional tasks as part of your action,
/// then you can call startAction prior to beginning your task, this will return a unique action id.
/// Perform any tasks (frontendapi requests, data processing etc).
/// You can then notifiy the user about errors or display a success notification.
/// Finally call stopAction while passing in the unique action id returned from startAction.
/// startAction and stopAction used in conjunction with DisableOnActionInProgress allow you to disable buttons while an action is in progress.

function startAction() {
    const actionUniqueId = generateUuid();
    ActionInProgressActions.actionStarted(actionUniqueId);
    return actionUniqueId;
}

function stopAction(actionUniqueId) {
    ActionInProgressActions.actionCompleted(actionUniqueId);
}

async function executeRequest(fetchFunction, successTitle, successMessage, children) {
    const actionUniqueId = startAction();
    try {
        return await executeRequestWithNoActionNotification(fetchFunction, successTitle, successMessage, children);
    }
    finally {
        stopAction(actionUniqueId);
    }
}

async function executeRequestWithNoActionNotification(fetchFunction, successTitle, successMessage, successChildren) {
    try {
        const result = await fetchFunction();
        if(successTitle && (successMessage || successChildren)) {
            NotificationActions.notificationReceived(
                Object.assign({
                    icon: 'check',
                    severity: 'success',
                    title: successTitle
                },
                successMessage ? { messages: [successMessage] } : null,
                successChildren ? { children: successChildren } : null));
        }

        return result;
    }
    catch(e) {
        if(!e.handled)
            ErrorHandler.handleError(e);
        throw(e instanceof Error ? e : new Error(e));
    }
}

export default { executeRequest, executeRequestWithNoActionNotification, startAction, stopAction };
